import { Text } from '@/components/ui/text';
import React from 'react';
import { View } from 'react-native';
import { isEntryVisible } from '../gating';
import type { FlagMap } from '../gating';
import type { SettingSection, SettingsEntry } from '../registry';
import { entriesForSection, SECTION_TITLES } from '../registry';
import { RegistryRow } from './registry-row';

interface SettingsRegistrySectionProps {
  section: SettingSection;
  flags: FlagMap;
}

/**
 * Renders one settings section: filters the registry to this section, applies
 * platform/flag visibility gating, sorts by order, and renders each surviving
 * entry. A section with no visible entries renders nothing.
 */
export function SettingsRegistrySection({
  section,
  flags,
}: SettingsRegistrySectionProps) {
  const visible: SettingsEntry[] = entriesForSection(section).filter((entry) =>
    isEntryVisible(entry, { flags }),
  );

  if (visible.length === 0) return null;

  return (
    <View className="mb-6">
      <Text
        className="text-sm mb-2 uppercase font-medium text-muted-foreground"
        accessibilityRole="header"
      >
        {SECTION_TITLES[section]}
      </Text>
      <View className="rounded-xl p-4 bg-card">
        {visible.map((entry, index) => (
          <RegistryRow
            key={rowKey(entry)}
            entry={entry}
            isLast={index === visible.length - 1}
          />
        ))}
      </View>
    </View>
  );
}

/** A stable React key per entry, independent of array position. */
function rowKey(entry: SettingsEntry): string {
  switch (entry.type) {
    case 'control':
      return `control-${entry.settingKey}`;
    case 'custom':
      return `custom-${entry.id}`;
    case 'permission':
      return `permission-${entry.permissionKey}`;
    default:
      return 'unknown';
  }
}
